import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Col, Form, Row } from 'react-bootstrap';
import NioButton from '../NioButton/NioButton';
import NioIcon from '../NioIcon/NioIcon';
import api from '../../utils/api';
import { useTranslation } from '../../i18n/I18nProvider';

const DATE_LOCALES = { fr: 'fr-FR', en: 'en-US' };

function getStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function StarDisplay({ rating, size = 'md', t }) {
  const stars = [1, 2, 3, 4, 5];
  const fontSize = size === 'sm' ? '0.95rem' : '1.15rem';
  return (
    <span className="d-inline-flex gap-1" aria-label={t('providerReviews.ratingAria', { rating })}>
      {stars.map((n) => (
        <span
          key={n}
          style={{ color: n <= Math.round(rating) ? '#f59e0b' : '#d1d5db', fontSize, display: 'inline-flex' }}
        >
          <NioIcon name="star-fill" />
        </span>
      ))}
    </span>
  );
}

function StarPicker({ value, onChange, disabled, t }) {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div className="d-flex gap-2 align-items-center flex-wrap">
      {stars.map((n) => (
        <button
          key={n}
          type="button"
          className="btn btn-link p-0 border-0 text-decoration-none"
          style={{ fontSize: '1.6rem', lineHeight: 1, color: n <= value ? '#f59e0b' : '#d1d5db' }}
          onClick={() => onChange(n)}
          disabled={disabled}
          aria-label={t('providerReviews.rateAria', { n })}
        >
          <NioIcon name="star-fill" />
        </button>
      ))}
      <span className="text-muted small">{value ? `${value}/5` : t('providerReviews.pickRating')}</span>
    </div>
  );
}

export default function ProviderReviewSection({
  providerId,
  providerName,
  serviceId,
  serviceTitle,
  providerEmail,
}) {
  const { t, language } = useTranslation();
  const location = useLocation();
  const dateLocale = DATE_LOCALES[language] || 'fr-FR';
  const [user, setUser] = useState(getStoredUser);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ averageRating: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  const formatDate = useCallback((value) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleDateString(dateLocale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return '-';
    }
  }, [dateLocale]);

  const isOwnProfile = useMemo(() => {
    if (!user?.email || !providerEmail) return false;
    return String(user.email).trim().toLowerCase() === String(providerEmail).trim().toLowerCase();
  }, [user?.email, providerEmail]);

  const myReview = useMemo(() => {
    if (!user?.email) return null;
    const email = String(user.email).trim().toLowerCase();
    return reviews.find((r) => String(r.userEmail).trim().toLowerCase() === email) || null;
  }, [reviews, user?.email]);

  const loadReviews = async () => {
    if (!providerId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.getProviderReviews(providerId);
      if (res?.success) {
        setReviews(Array.isArray(res.data) ? res.data : []);
        setStats(res.stats || { averageRating: 0, count: 0 });
      }
    } catch (e) {
      const msg = e.message || '';
      if (msg.includes('404') || msg.includes('Route introuvable')) {
        setReviews([]);
        setStats({ averageRating: 0, count: 0 });
      } else {
        setError(msg || t('providerReviews.loadError'));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setUser(getStoredUser());
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  useEffect(() => {
    if (myReview) {
      setRating(myReview.rating || 0);
      setComment(myReview.comment || '');
    }
  }, [myReview]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!user?.email) return;
    if (!rating || rating < 1) {
      setError(t('providerReviews.ratingRequired'));
      return;
    }
    if (!comment.trim()) {
      setError(t('providerReviews.commentRequired'));
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.submitProviderReview({
        providerId,
        providerName,
        userId: user.id || user._id,
        userName: [user.name, user.lastName].filter(Boolean).join(' ') || user.name,
        userEmail: user.email,
        serviceId,
        serviceTitle,
        rating,
        comment: comment.trim(),
      });
      if (res?.success) {
        setSuccess(myReview ? t('providerReviews.updated') : t('providerReviews.published'));
        if (res.stats) setStats(res.stats);
        await loadReviews();
      } else {
        setError(res?.message || t('providerReviews.submitError'));
      }
    } catch (err) {
      setError(err.message || t('providerReviews.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="provider-reviews mt-4 pt-4 border-top">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h4 className="mb-1">{t('providerReviews.title')}</h4>
          <p className="text-muted mb-0 small">{t('providerReviews.subtitle')}</p>
        </div>
        {stats.count > 0 ? (
          <div className="text-end">
            <div className="d-flex align-items-center gap-2 justify-content-end">
              <span className="fs-3 fw-bold mb-0">{stats.averageRating}</span>
              <StarDisplay rating={stats.averageRating} t={t} />
            </div>
            <div className="text-muted small">{t('providerReviews.reviewsCount', { count: stats.count })}</div>
          </div>
        ) : (
          <div className="text-muted small">{t('providerReviews.noReviews')}</div>
        )}
      </div>

      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      {success ? <div className="alert alert-success py-2">{success}</div> : null}

      {!user ? (
        <div className="bg-light rounded p-3 mb-4">
          <p className="mb-2 mb-md-0">{t('providerReviews.loginPrompt')}</p>
          <Link to="/auth/login" state={{ from: location.pathname }} className="btn btn-primary btn-sm">
            {t('providerReviews.login')}
          </Link>
        </div>
      ) : null}

      {user && !isOwnProfile ? (
        <div className="border rounded p-3 p-md-4 mb-4 bg-white">
          <h5 className="mb-3">{myReview ? t('providerReviews.editReview') : t('providerReviews.leaveReview')}</h5>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>{t('providerReviews.ratingLabel')}</Form.Label>
              <StarPicker value={rating} onChange={setRating} disabled={submitting} t={t} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('providerReviews.commentLabel')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                maxLength={1000}
                placeholder={t('providerReviews.commentPlaceholder')}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={submitting}
              />
            </Form.Group>
            <NioButton
              type="submit"
              className="btn-primary"
              label={submitting ? t('providerReviews.submitting') : (myReview ? t('providerReviews.updateReview') : t('providerReviews.publishReview'))}
              disabled={submitting}
            />
          </Form>
        </div>
      ) : null}

      {user && isOwnProfile ? (
        <div className="alert alert-info py-2">{t('providerReviews.ownProfile')}</div>
      ) : null}

      {loading ? (
        <div className="text-muted py-3">{t('providerReviews.loading')}</div>
      ) : reviews.length === 0 ? (
        <div className="text-muted py-2">{t('providerReviews.beFirst')}</div>
      ) : (
        <Row className="gy-3">
          {reviews.map((review) => (
            <Col xs={12} key={review._id}>
              <div className="border rounded p-3 h-100">
                <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                  <div>
                    <strong>{review.userName || review.userEmail || t('providerReviews.client')}</strong>
                    {review.serviceTitle ? (
                      <span className="text-muted small ms-2">· {review.serviceTitle}</span>
                    ) : null}
                  </div>
                  <span className="text-muted small">{formatDate(review.updatedAt || review.createdAt)}</span>
                </div>
                <div className="mb-2">
                  <StarDisplay rating={review.rating} size="sm" t={t} />
                </div>
                <p className="mb-0">{review.comment}</p>
              </div>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
}
